import classNames from 'classnames';
import { ReactNode } from 'react';
import { LearnLink, LearnLinkProps } from './LearnLink';

interface Props {
  eyebrow: string;
  title: ReactNode;
  description: string;
  /** Optional — the "learn more" links block is omitted entirely when empty. */
  learnLinks?: LearnLinkProps[];
  visual: ReactNode;
  /** When true, the copy sits on the left and the visual on the right (desktop). */
  reversed?: boolean;
}

export const FeatureRow = ({
  eyebrow,
  title,
  description,
  learnLinks,
  visual,
  reversed = false,
}: Props) => (
  <section className={styles.section}>
    <div className={styles.wrap}>
      <div className={styles.grid}>
        {/* Visual */}
        <div
          className={classNames(
            styles.visual.container,
            reversed && 'lg:order-last',
          )}
        >
          <div className={styles.visual.glow} />
          <div className={styles.visual.stack}>{visual}</div>
        </div>

        {/* Copy */}
        <div className={styles.copy.container}>
          <div className={styles.copy.eyebrow}>{eyebrow}</div>
          <h2 className={styles.copy.title}>{title}</h2>
          <p className={styles.copy.description}>{description}</p>
          {learnLinks && learnLinks.length > 0 && (
            <div className={styles.copy.learn}>
              {learnLinks.map((link) => (
                <LearnLink key={link.label} {...link} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  </section>
);

const styles = {
  section: 'marketing-dark:bg-mf-navy',
  wrap: 'max-w-[1200px] mx-auto px-6 lg:px-10',
  grid: classNames(
    'grid grid-cols-1 items-center gap-[34px] py-[54px]',
    'lg:grid-cols-[1.05fr_0.95fr] lg:gap-[70px] lg:py-[74px]',
  ),
  visual: {
    container: 'relative flex min-h-[380px] items-center justify-center',
    // Pastel-blue halo in the light theme, deep blue glow in the dark one.
    glow: classNames(
      'pointer-events-none absolute h-[440px] w-[520px] max-w-full rounded-full',
      'bg-[radial-gradient(circle,rgba(189,222,255,0.5),transparent_64%),radial-gradient(circle_at_24%_76%,rgba(189,222,255,0.3),transparent_58%)]',
      'marketing-dark:bg-[radial-gradient(circle,rgba(40,80,200,0.28),transparent_64%)]',
    ),
    stack: 'relative z-[2] flex w-full justify-center',
  },
  copy: {
    container: '',
    eyebrow:
      'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
    title:
      'text-[28px] font-bold leading-[1.15] tracking-[-0.01em] text-mf-off-black-600 marketing-dark:text-white sm:text-[31px]',
    description:
      'mt-4 text-[16.5px] leading-[1.7] text-mf-gray-600 marketing-dark:text-white/64',
    learn: 'mt-[26px] flex flex-col gap-[13px]',
  },
};
